// src/components/DashboardComponent/AddResearchForm.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { addResearch, updateResearch } from '../../utils/researchApi';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2.5rem;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  }
`;

const FormTitle = styled.h2`
  font-family: 'Chau Philomene One', sans-serif;
  font-size: 2.2rem;
  color: #136F07;
  text-align: center;
  margin-bottom: 2rem;
  letter-spacing: 1px;
`;

const ErrorMessage = styled.div`
  background: #ffe5e5;
  color: #b00020;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  border-left: 5px solid #b00020;
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  background: #e6ffed;
  color: #0f5132;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  border-left: 5px solid #0f5132;
  font-weight: 500;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  border: 1px solid #e0e0e0;
  padding: 1.5rem;
  border-radius: 12px;
  background: #f9f9f9;
`;

const SectionTitle = styled.h3`
  margin-top: 0;
  color: #248517;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.95rem;
  color: #444;
`;

const Input = styled.input`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  color: #000;
  background: #fff;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: #136F07;
    box-shadow: 0 0 0 3px rgba(19, 111, 7, 0.2);
    outline: none;
  }
`;

const Textarea = styled.textarea`
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  color: #000; 
  background: #fff;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    border-color: #136F07;
    box-shadow: 0 0 0 3px rgba(19, 111, 7, 0.2);
    outline: none;
  }
`;

const Select = styled.select`
  padding: 0.8rem;
  border: 1px solid #2a9c0d73;
  border-radius: 8px;
  font-size: 1rem;
  color: #047311d3; 
  background: #46b31b13; /* Changed background color */
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #23e00aff; /* Changed focus border color */
    outline: none;
  }
`;

const FileInput = styled(Input)`
  padding: 0.6rem;
`;

const SubmitButton = styled.button`
  background: #136F07;
  color: #fff;
  padding: 1rem 2rem;
  border: none;
  border-radius: 30px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.3s ease;

  &:hover {
    background: #0f5a05;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(19, 111, 7, 0.3);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const AddResearchForm = ({ onSuccess, initialData = null }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ONLY ALLOWED TYPES (Capitalized, matching display expectations)
  const RESEARCH_TYPES = [
    { value: 'Journal', label: 'Journal' },
    { value: 'Conference', label: 'Conference' },
    { value: 'Thesis', label: 'Thesis' }, 
    { value: 'Capstone', label: 'Capstone Project' }, 
    { value: 'Report', label: 'Research Report' },
    { value: 'Others', label: 'Others (Specify)' } 
  ];

  // This runs ONCE on component mount or when initialData changes
  const initialTypeValue = initialData?.type && 
    initialData.type.trim() !== '' &&
    RESEARCH_TYPES.some(t => t.value === initialData.type.trim())
      ? initialData.type.trim()
      : 'Journal';

  const [typeValue, setTypeValue] = useState(initialTypeValue);

   const handleTypeChange = (e) => {
    const newType = e.target.value;
    console.log('Type changed to:', newType); 
    setTypeValue(newType); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const payload = new FormData();

      // Required fields
      payload.append('title', e.target.title.value);
      payload.append('description', e.target.description.value);

      
      
      // Use typeValue state which is always kept in sync
      const finalType = typeValue || 'Journal';
      console.log('📝 Submitting research with type:', finalType); 
      payload.append('type', finalType);
      
      payload.append('date', e.target.date.value);

      // Optional fields
      payload.append('subtitle', e.target.subtitle?.value || '');
      payload.append('abstract', e.target.abstract?.value || '');
      payload.append('link', e.target.link?.value || '');
      payload.append('authors', e.target.authors?.value || '');
      payload.append('orcid', e.target.orcid?.value || '');
      payload.append('keywords', e.target.keywords?.value || '');
      payload.append('submissionDate', e.target.submissionDate?.value || '');
      payload.append('revisionDate', e.target.revisionDate?.value || '');
      payload.append('acceptanceDate', e.target.acceptanceDate?.value || '');
      payload.append('publicationDate', e.target.publicationDate?.value || '');
      payload.append('license', e.target.license?.value || '');
      payload.append('funding', e.target.funding?.value || '');
      payload.append('conflict', e.target.conflict?.value || '');

      const publishedValue = e.target.published.value === 'true' ? '1' : '0';
      payload.append('published', publishedValue);

      // File upload (if selected)
      if (file) {
        payload.append('file', file);
      }

      let result;
      if (initialData && initialData.id) {
        // Update existing research
        result = await updateResearch(initialData.id, payload);
      } else {
        result = await addResearch(payload);
      }

     setSuccess(result.message || (initialData ? 'Research updated successfully!' : 'Research added successfully!'));

      if (!initialData) {
        e.target.reset();
        setFile(null);
        setTypeValue('Journal');
      } 

      if (onSuccess) {
        setTimeout(() => onSuccess(), 1200);
      } else {
        setTimeout(() => {
          navigate('/admin/research');
        }, 1600);
      }

    } catch (err) {
      console.error('❌ Submission error:', err);
      setError(err.message || 'Failed to add research. Please try again.');

      if (err.message.includes('log in') || err.message.includes('401') || err.message.includes('authenticated')) {
        setTimeout(() => {
          alert('Session expired. Please log in again.');
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          navigate('/login');
        }, 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <FormTitle>{initialData ? 'Edit Research' : 'Add New Research'}</FormTitle>

      {/* Error Alert */}
      {error && (
        <ErrorMessage>
          ❌ <strong>Error:</strong> {error}
        </ErrorMessage>
      )}

      {/* Success Alert */}
      {success && (
        <SuccessMessage>
          ✅ {success}
        </SuccessMessage>
      )}

      <Form onSubmit={handleSubmit}>

        {/* Basic Info */}
        <Section>
          <SectionTitle>Basic Information</SectionTitle>
          <InputGroup>
            <Label>Title *</Label>
            <Input name="title" required disabled={loading} defaultValue={initialData?.title || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Subtitle</Label>
            <Input name="subtitle" disabled={loading} defaultValue={initialData?.subtitle || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Authors</Label>
            <Input name="authors" placeholder="Kyla Quire, Juan Dela Cruz" disabled={loading} defaultValue={initialData?.authors || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Description *</Label>
            <Textarea name="description" required disabled={loading} defaultValue={initialData?.description || ''} />
          </InputGroup>
          <InputGroup>
            <Label>What type of research is this? *</Label>
            <Select
              name="type"
              value={typeValue}
             onChange={handleTypeChange} 
              required
              disabled={loading}
            >
              {RESEARCH_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </InputGroup>
          <InputGroup>
            <Label>Publication Date *</Label>
            <Input name="date" type="date" required disabled={loading} defaultValue={initialData?.date || ''} />
          </InputGroup>
          <InputGroup>
            <Label>External Link</Label>
            <Input name="link" type="url" placeholder="https://..." disabled={loading} defaultValue={initialData?.link || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Upload File (PDF/Word)</Label>
            <FileInput
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={loading}
            />
          </InputGroup>
        </Section>

        {/* Metadata  */}{/*as of now dili pani gamiton */}
        <Section>
          <SectionTitle>Metadata</SectionTitle>
          <InputGroup>
            <Label>ORCID IDs</Label>
            <Input name="orcid" placeholder="e.g., 0000-0000-0000-0000" disabled={loading} defaultValue={initialData?.orcid || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Keywords</Label>
            <Input name="keywords" placeholder="comma-separated" disabled={loading} defaultValue={initialData?.keywords || ''} />
          </InputGroup>
          <InputGroup>
            <Label>License</Label>
            <Input name="license" placeholder="e.g., CC BY 4.0" disabled={loading} defaultValue={initialData?.license || ''} />
          </InputGroup>
        </Section>

        {/* Abstract */}{/* display pani  */}
        <Section>
          <SectionTitle>Abstract</SectionTitle>
          <Textarea name="abstract" placeholder="Enter abstract..." disabled={loading} defaultValue={initialData?.abstract || ''} />
        </Section>

        {/* Dates */}
        <Section>
          <SectionTitle>Internal Dates</SectionTitle>
          <InputGroup>
            <Label>Submission Date</Label>
            <Input name="submissionDate" type="date" disabled={loading} defaultValue={initialData?.submissionDate || ''} />
          </InputGroup>
          <InputGroup>{/*  Dispay pani*/}
            <Label>Revision Date</Label>
            <Input name="revisionDate" type="date" disabled={loading} defaultValue={initialData?.revisionDate || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Acceptance Date</Label>{/* Display pani */}
            <Input name="acceptanceDate" type="date" disabled={loading} defaultValue={initialData?.acceptanceDate || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Publication Date (Final)</Label>
            <Input name="publicationDate" type="date" disabled={loading} defaultValue={initialData?.publicationDate || ''} />
          </InputGroup>
        </Section>

        {/* Funding & Ethics */}{/* display pni */}
        <Section>
          <SectionTitle>Funding & Ethics</SectionTitle>
          <InputGroup>
            <Label>Funding Source</Label>
            <Textarea name="funding" disabled={loading} defaultValue={initialData?.funding || ''} />
          </InputGroup>
          <InputGroup>
            <Label>Conflict of Interest</Label>
            <Textarea name="conflict" disabled={loading} defaultValue={initialData?.conflict || ''} />
          </InputGroup>
        </Section>

        {/* Publish Toggle */}
        <Section>
          <InputGroup>
            <Label>Publish Immediately?</Label>
            <Select name="published" defaultValue={initialData ? (initialData.published === '1' ? 'true' : 'false') : 'true'} disabled={loading}>
              <option value="true">Yes</option>
              <option value="false">No (keep unpublished)</option>
            </Select>
          </InputGroup>
        </Section>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? (initialData ? '⏳ Updating...' : '⏳ Adding Research...') : (initialData ? 'Save Changes' : 'Add Research')}
        </SubmitButton>
      </Form>
    </FormContainer>
  );
};

export default AddResearchForm;